import { useMemo } from "react"
import { useStaticQuery, graphql } from "gatsby"
import { useIntl } from "react-intl"

/**
 * Check if two cosmic school objects have the same locale, state/region, size
 * filters, compatible institution type fields, and are not the same school.
 * 
 * The institution type (undergrad, technical, and graduate) fields are checked
 * as follows (the reasoning/intention was not documented in the code this
 * functionality was taken from):
 * - if schoolA is both an undergrad and technical school, only check the
 *   graduate and undergrad fields
 * - if schoolA is a technical but not an undergrad school, only check the
 *   technical and undergrad fields
 * - otherwise, check all three institution type fields
 * 
 * @param {Object} schoolA - Cosmic school object  
 * @param {Object} schoolB - Cosmic school object  
 * @returns {boolean}
 */
const schoolSizeLocationAndTypesMatch = (schoolA, schoolB) => {
  if (schoolA.locale !== schoolB.locale 
      || schoolA.slug === schoolB.slug
      || schoolA.metadata.school_state_or_region !== schoolB.metadata.school_state_or_region
      || !schoolA.metadata.school_size_filter?.length
      || !schoolB.metadata.school_size_filter?.length
      || schoolA.metadata.school_size_filter.join(",") !== schoolB.metadata.school_size_filter.join(",")) {
    return false
  }
  
  let fieldsToCompare = ["school_technical_type", "school_undergrad_type", "school_graduate_type"]
  const { school_technical_type, school_undergrad_type } = schoolA.metadata
  if (school_undergrad_type && school_technical_type) {
    fieldsToCompare = ["school_graduate_type", "school_undergrad_type"]
  } else if (!school_undergrad_type && school_technical_type) {
    fieldsToCompare = ["school_technical_type", "school_undergrad_type"]
  }
  return fieldsToCompare.every(field => schoolA.metadata[field] === schoolB.metadata[field])
}

/**
 * Fetch all schools that match a given school's size filters, region/state,
 * and institution types
 * @param {string} schoolSlug - ignore schools with this cosmic slug
 * @returns {Object[]} schools with the same values for the provided fields
 */
export const useSimilarSizeLocationSchools = (schoolSlug) => {
  const { locale } = useIntl()
  const { allSchools } = useStaticQuery(graphql`
    query {
      allSchools: allCosmicjsSchools {
        nodes {
          locale
          slug
          title
          metadata {
            school_size_filter
            school_state_or_region
            school_graduate_type
            school_technical_type
            school_undergrad_type
          }
        }
      }
    }
  `)

  const matchingSchools = useMemo(() => {
    const schoolNode = allSchools.nodes.find(node => 
      node.slug === schoolSlug && node.locale === locale
    )
    if (!schoolNode) {
      return []
    }
    return allSchools.nodes.filter(node => schoolSizeLocationAndTypesMatch(schoolNode, node))
  }, [allSchools.nodes, schoolSlug, locale])
  
  return matchingSchools
}
